import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgSelectModule } from '@ng-select/ng-select'
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { PanelComponent } from '@components/core-template/panel/panel.component';
import { MigrationService } from '@services/migration.service';

@Component({
  selector: 'app-migration-group',
  templateUrl: './migration-group.component.html',
  styleUrls: ["./migration-group.component.css"],
  imports: [PanelComponent, FormsModule, ReactiveFormsModule, NgSelectModule, DatePipe],
  providers: [MigrationService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MigrationGroupComponent implements OnInit {

  private _service = inject(MigrationService)
  private _fb = inject(FormBuilder)
  private destroyRef = inject(DestroyRef)
  private cdr = inject(ChangeDetectorRef)

  protected policies: any[] = []
  protected originGroups: any[] = []
  protected destinationGroups: any[] = []
  protected preview: any[] = []
  protected previewLoaded = false
  protected previewRequests: any[] = []
  protected previewRequestsLoaded = false
  protected resultCharges: any = null
  protected resultRequests: any = null

  protected form = this._fb.group({
    policyId: [null, Validators.required],
    originGroupId: [null, Validators.required],
    destinationGroupId: [null, Validators.required],
    periodFrom: [new Date().toISOString().slice(0, 7)],
  })

  ngOnInit(): void {
    this.loadPolicies()
    this.loadDestinationGroups()
  }

  loadPolicies() {
    this._service.getPoliciesByYear(new Date().getFullYear()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.policies = res
      this.cdr.markForCheck()
    })
  }

  onPolicyChange() {
    this.form.patchValue({ originGroupId: null })
    this.originGroups = []
    this.preview = []
    this.previewLoaded = false
    const policyId = this.form.value.policyId
    if (!policyId) return
    this._service.getGroupsByPolicy(policyId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      const seen = new Set<string>()
      this.originGroups = res.filter((g: any) => {
        if (seen.has(g.name)) return false
        seen.add(g.name)
        return true
      })
      this.cdr.markForCheck()
    })
  }

  onOriginGroupChange() {
    this.preview = []
    this.previewLoaded = false
    this.previewRequests = []
    this.previewRequestsLoaded = false
    const policy = this.policies.find(p => p._id === this.form.value.policyId)
    const origin = this.originGroups.find(g => g._id === this.form.value.originGroupId)
    if (!policy || !origin) return
    this.loadChargesPreview(policy.code, origin.name)
    this.loadRequestsPreview(origin.name)
  }

  onPeriodChange() {
    const origin = this.originGroups.find(g => g._id === this.form.value.originGroupId)
    if (!origin) return
    this.previewRequests = []
    this.previewRequestsLoaded = false
    this.loadRequestsPreview(origin.name)
  }

  loadChargesPreview(policyCode: number, originName: string) {
    this._service.previewGroup(policyCode, originName).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.preview = res
      this.previewLoaded = true
      this.cdr.markForCheck()
    })
  }

  loadRequestsPreview(originName: string) {
    this._service.previewGroupRequests(originName, this.form.value.periodFrom ?? undefined).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.previewRequests = res
      this.previewRequestsLoaded = true
      this.cdr.markForCheck()
    })
  }

  loadDestinationGroups() {
    this._service.getAllGroups(new Date().getFullYear()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      const seen = new Set<string>()
      this.destinationGroups = res.filter((g: any) => {
        if (seen.has(g.name)) return false
        seen.add(g.name)
        return true
      })
      this.cdr.markForCheck()
    })
  }

  executeCharges() {
    const policy = this.policies.find(p => p._id === this.form.value.policyId)
    const origin = this.originGroups.find(g => g._id === this.form.value.originGroupId)
    const destination = this.destinationGroups.find(g => g._id === this.form.value.destinationGroupId)

    if (!policy || !origin || !destination) return

    this.confirmAndRun(
      '¿Confirmar reasignación de cobranzas?',
      `<b>Poliza:</b> ${policy.code}<br>
       <b>Grupo origen:</b> ${origin.name}<br>
       <b>Grupo destino:</b> ${destination.name}<br>
       <b>Asegurados a migrar:</b> ${this.preview.length}`,
      () => this.runCharges(policy, origin, destination)
    )
  }

  executeRequests() {
    const origin = this.originGroups.find(g => g._id === this.form.value.originGroupId)
    const destination = this.destinationGroups.find(g => g._id === this.form.value.destinationGroupId)

    if (!origin || !destination) return

    this.confirmAndRun(
      '¿Confirmar reasignación de movimientos?',
      `<b>Grupo origen:</b> ${origin.name}<br>
       <b>Grupo destino:</b> ${destination.name}<br>
       <b>Movimientos a migrar:</b> ${this.previewRequests.length}`,
      () => this.runRequests(origin, destination)
    )
  }

  private confirmAndRun(title: string, html: string, run: () => void) {
    Swal.fire({
      title,
      html,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ejecutar',
      cancelButtonText: 'Cancelar'
    }).then(result => {
      if (result.isConfirmed) run()
    })
  }

  private runCharges(policy: any, origin: any, destination: any) {
    this._service.reassignGroupCharges({
      policyCode: policy.code,
      originGroupName: origin.name,
      destinationGroupName: destination.name,
      periodFrom: this.form.value.periodFrom ?? undefined
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.resultCharges = res
        Swal.fire('Reasignación completada', `Registros actualizados: ${res.affectedRows}`, 'success')
        this.cdr.markForCheck()
      },
      error: err => {
        Swal.fire('Error', err.error?.userMessage || 'Error al reasignar grupo', 'error')
        this.cdr.markForCheck()
      }
    })
  }

  private runRequests(origin: any, destination: any) {
    this._service.reassignGroupRequests({
      originGroupName: origin.name,
      destinationGroupName: destination.name,
      periodFrom: this.form.value.periodFrom ?? undefined
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.resultRequests = res
        Swal.fire('Reasignación completada', `Movimientos actualizados: ${res.requestsUpdated}`, 'success')
        this.cdr.markForCheck()
      },
      error: err => {
        Swal.fire('Error', err.error?.userMessage || 'Error al reasignar grupo', 'error')
        this.cdr.markForCheck()
      }
    })
  }
}
